/* ============================================================
   SHAKOM — Receipt Printer (animación de compra)

   Puerto a JS/CSS vanilla del componente React "ReceiptPrinter"
   (Framer Motion + Tailwind) que trajiste en receipt printer
   codigo.txt. Mismo comportamiento — 3 estados (processing /
   printing / complete), recibo que sale de la impresora con
   avance "a trompicones" (feedMotion stepped) o continuo
   (smooth), respeta prefers-reduced-motion — adaptado a este
   sitio (sin build de React/Tailwind), así que las texturas de
   plástico/papel del original se reemplazan por sombras y
   gradientes planos: no hay esos assets en este proyecto.

   Todavía NO está conectado al botón "Comprar ahora" de
   producto.html — ver receipt-printer-demo.html para probarla,
   y el comentario en js/product.js con el punto de enganche para
   cuando quieras usarla en serio (cuando haya SHOP_DOMAIN real).

   Uso:
     const printer = ReceiptPrinter.create(mountEl, {
       stage: 'processing',       // 'processing' | 'printing' | 'complete'
       animate: true,
       feedMotion: 'stepped',     // 'stepped' | 'smooth'
       brand: 'SHAKOM',
       paperHTML: '<p>...</p>',   // contenido del recibo
     });
     printer.setStage('printing');
     printer.setStage('complete');
     printer.destroy();
   ============================================================ */
(function (global) {
  'use strict';

  const EASE_OUT = [0.23, 1, 0.32, 1];
  const EASE_IN_OUT = [0.77, 0, 0.175, 1];

  const PRINTING_TRANSFORMS = [
    'translateY(calc(-100% + 2px))',
    'translateY(-91%)', 'translateY(-91%)',
    'translateY(-81%)', 'translateY(-81%)',
    'translateY(-70%)', 'translateY(-70%)',
    'translateY(-58%)', 'translateY(-58%)',
    'translateY(-45%)', 'translateY(-45%)',
    'translateY(-32%)', 'translateY(-32%)',
    'translateY(-20%)', 'translateY(-20%)',
    'translateY(-10%)', 'translateY(-10%)',
    'translateY(-3%)', 'translateY(-3%)',
    'translateY(0%)',
  ];
  const PRINTING_TIMES = [
    0, 0.075, 0.105, 0.18, 0.21, 0.285, 0.315, 0.39, 0.42, 0.495,
    0.525, 0.6, 0.63, 0.705, 0.735, 0.81, 0.84, 0.915, 0.945, 1,
  ];

  const STATUS_LABELS = {
    processing: 'Procesando tu pedido',
    printing: 'Imprimiendo tu recibo',
    complete: 'Pedido completo',
  };

  const SPINNER_SVG =
    '<svg viewBox="0 0 256 256" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<circle cx="128" cy="128" r="96" stroke="currentColor" stroke-width="22" stroke-linecap="round" stroke-dasharray="430 173"/>' +
    '</svg>';
  const CHECK_SVG =
    '<svg viewBox="0 0 256 256" width="16" height="16" xmlns="http://www.w3.org/2000/svg">' +
    '<path fill="currentColor" d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm49.66,85.66-58.34,58.34a8,8,0,0,1-11.32,0l-29.66-29.66a8,8,0,0,1,11.32-11.32L112,149.37l52.34-52.35a8,8,0,0,1,11.32,11.32Z"/>' +
    '</svg>';

  function cubicBezier(curve) {
    return `cubic-bezier(${curve.join(',')})`;
  }

  function buildReceiptClipPath() {
    const toothCount = 40;
    const toothDepth = 4;
    const points = [];
    for (let index = 0; index < toothCount * 2; index++) {
      const x = 100 - ((index + 1) * 100) / (toothCount * 2);
      const y = index % 2 === 0 ? '100%' : `calc(100% - ${toothDepth}px)`;
      points.push(`${x}% ${y}`);
    }
    return `polygon(0 0, 100% 0, 100% calc(100% - ${toothDepth}px), ${points.join(', ')})`;
  }

  const RECEIPT_CLIP_PATH = buildReceiptClipPath();

  const MARKUP = `
    <div class="rp-machine">
      <div class="rp-header">
        <span class="rp-brand"></span>
        <div class="rp-status">
          <span class="rp-status__icon" aria-hidden="true"></span>
          <span class="rp-status__label" role="status" aria-live="polite"></span>
        </div>
      </div>
      <div class="rp-screen"><div class="rp-slot"></div></div>
    </div>
    <div class="rp-output">
      <div class="rp-output__glow" aria-hidden="true"></div>
      <article class="rp-paper"></article>
    </div>
  `;

  function create(mount, options = {}) {
    const {
      stage: initialStage = 'processing',
      animate = true,
      feedMotion = 'stepped',
      brand = 'SHAKOM',
      paperHTML = '',
    } = options;

    const reduceMotion = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const shouldMove = animate && !reduceMotion;

    mount.classList.add('rp');
    mount.setAttribute('aria-label', 'Impresora de recibo');
    mount.innerHTML = MARKUP;

    mount.querySelector('.rp-brand').textContent = brand;
    const statusIcon = mount.querySelector('.rp-status__icon');
    const statusLabel = mount.querySelector('.rp-status__label');
    const paper = mount.querySelector('.rp-paper');
    paper.style.clipPath = RECEIPT_CLIP_PATH;
    paper.innerHTML = paperHTML;
    paper.style.opacity = '0';
    paper.style.transform = 'translateY(calc(-100% + 2px))';

    let currentStage = null;
    let transformAnim = null;
    let opacityAnim = null;

    function swapStatusIcon(stage) {
      const isComplete = stage === 'complete';
      const nextHTML = isComplete ? CHECK_SVG : SPINNER_SVG;
      statusIcon.classList.toggle('rp-status__icon--complete', isComplete);
      statusIcon.classList.toggle('rp-status__icon--spin', !isComplete && shouldMove);

      if (!animate) {
        statusIcon.innerHTML = nextHTML;
        return;
      }
      const outKeyframes = [
        { opacity: 1, transform: 'scale(1)' },
        { opacity: 0, transform: shouldMove ? 'scale(0.96)' : 'scale(1)' },
      ];
      const swap = statusIcon.animate(outKeyframes, { duration: 160, easing: cubicBezier(EASE_OUT), fill: 'forwards' });
      swap.finished.then(() => {
        statusIcon.innerHTML = nextHTML;
        statusIcon.animate(
          [
            { opacity: 0, transform: shouldMove ? 'scale(0.94)' : 'scale(1)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 160, easing: cubicBezier(EASE_OUT), fill: 'forwards' },
        );
      }).catch(() => {});
    }

    function swapStatusLabel(stage) {
      const text = STATUS_LABELS[stage];
      if (!animate) {
        statusLabel.textContent = text;
        return;
      }
      const out = statusLabel.animate(
        [
          { opacity: 1, transform: 'translateY(0px)' },
          { opacity: 0, transform: shouldMove ? 'translateY(-4px)' : 'translateY(0px)' },
        ],
        { duration: 180, easing: cubicBezier(EASE_OUT), fill: 'forwards' },
      );
      out.finished.then(() => {
        statusLabel.textContent = text;
        statusLabel.animate(
          [
            { opacity: 0, transform: shouldMove ? 'translateY(4px)' : 'translateY(0px)' },
            { opacity: 1, transform: 'translateY(0px)' },
          ],
          { duration: 180, easing: cubicBezier(EASE_OUT), fill: 'forwards' },
        );
      }).catch(() => {});
    }

    // Estado lógico ya aplicado (no se lee de getComputedStyle: cancelar
    // una animación con fill:forwards la revierte al último valor puesto
    // por .style directamente, no al valor visual con el que se rellenó,
    // así que llevamos la cuenta nosotros mismos).
    let currentOpacity = 0;
    let currentTransform = 'translateY(calc(-100% + 2px))';

    function animatePaper(stage) {
      const isVisible = stage !== 'processing';
      const opacityDuration = animate ? 160 : 0;
      const transformDuration = shouldMove ? 1750 : 0;
      const useSteppedFeed = feedMotion === 'stepped' && stage === 'printing' && shouldMove;

      const targetOpacity = isVisible ? 1 : 0;
      if (targetOpacity !== currentOpacity) {
        if (opacityAnim) opacityAnim.cancel();
        opacityAnim = paper.animate(
          [{ opacity: currentOpacity }, { opacity: targetOpacity }],
          { duration: opacityDuration, easing: cubicBezier(EASE_OUT), fill: 'forwards' },
        );
        currentOpacity = targetOpacity;
      }

      if (useSteppedFeed) {
        if (transformAnim) transformAnim.cancel();
        transformAnim = paper.animate(
          PRINTING_TRANSFORMS.map((transform, i) => ({ transform, offset: PRINTING_TIMES[i] })),
          { duration: transformDuration, easing: 'linear', fill: 'forwards' },
        );
        currentTransform = 'translateY(0%)';
        return;
      }

      const targetTransform = isVisible || !shouldMove ? 'translateY(0%)' : 'translateY(calc(-100% + 2px))';
      if (targetTransform !== currentTransform) {
        if (transformAnim) transformAnim.cancel();
        transformAnim = paper.animate(
          [{ transform: currentTransform }, { transform: targetTransform }],
          { duration: transformDuration, easing: cubicBezier(EASE_IN_OUT), fill: 'forwards' },
        );
        currentTransform = targetTransform;
      }
    }

    function setStage(stage) {
      if (stage === currentStage) return;
      currentStage = stage;
      mount.dataset.stage = stage;
      swapStatusIcon(stage);
      swapStatusLabel(stage);
      animatePaper(stage);
    }

    setStage(initialStage);

    return {
      setStage,
      setPaperHTML(html) { paper.innerHTML = html; },
      destroy() {
        if (transformAnim) transformAnim.cancel();
        if (opacityAnim) opacityAnim.cancel();
        mount.innerHTML = '';
        mount.classList.remove('rp');
      },
    };
  }

  global.ReceiptPrinter = { create };
})(window);
